import * as PIXI from "pixi.js";

import { EGameState, TSpinResponse } from "@src/server/ResponseTypes";
import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";
import { Spine } from "@pixi/spine-pixi";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";
import { GameScreen } from "../GameScreen";

// Displays win animations when the player hits a win
export class WinView implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _backgroundView: PIXI.Container = new PIXI.Container();
  public get backgroundView(): PIXI.Container {
    return this._backgroundView;
  }

  private _winSkeleton!: Spine;
  private _winAmountText!: PIXI.BitmapText;
  private _backgroundGraphics!: PIXI.Graphics;

  private _totalWinFadeAnimationTween!: GSAPTween;
  private _totalWinCountAnimationTween!: GSAPTween;
  private _activeWinDelayTween!: GSAPTween;

  public constructor() {
    this._setupEventListeners();

    this._initializeAssets();
    this._parentAssets();

    this._initializeComponents();
    this._parentComponents();

    this._view.zIndex = 20;
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_PERFORM_ACTION,
      this._onPerformedGameAction.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.REEL_PANEL_SPIN_STOPPED,
      this._onReelPanelSpinStopped.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SPIN_STOPPED,
      this._onCellPanelSpinStopped.bind(this),
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_INTERACTABLE_AREA,
      this._onClickedInteractableArea.bind(this),
    );
  }

  private _initializeAssets(): void {
    this._winSkeleton = Spine.from({
      skeleton: "winsAtlasJson",
      atlas: "winsAtlas",
    });
    this._winSkeleton.scale.set(0.2);
  }

  private _parentAssets(): void {
    this._view.addChild(this._winSkeleton);
  }

  private _initializeComponents(): void {
    this._winAmountText = new PIXI.BitmapText({
      text: "123456.00",
      style: {
        fontSize: 100,
        fontFamily: "winFont",
        fill: "0xffffff",
      },
    });
    this._winAmountText.anchor.set(0.5);
    this._winAmountText.position.set(0, 150);
    this._winAmountText.visible = false;

    this._backgroundGraphics = new PIXI.Graphics();
    this._backgroundGraphics
      .rect(-2000, -2000, 4000, 4000)
      .fill({ color: 0x000000, alpha: 0.7 });
    this._backgroundGraphics.zIndex = -1;
    this._backgroundGraphics.visible = false;
  }

  private _parentComponents(): void {
    this._view.addChild(this._winAmountText);
    this._backgroundView.addChild(this._backgroundGraphics);
  }

  private _onPerformedGameAction(_: TSpinResponse): void {
    console.log("killed active win delay tween");
    this._activeWinDelayTween?.kill();

    this._totalWinFadeAnimationTween?.kill();
    this._totalWinCountAnimationTween?.kill();

    this._backgroundGraphics.visible = false;
    this._winAmountText.visible = false;
    this._winSkeleton.state.timeScale = 1;
  }

  private _onReelPanelSpinStopped(): void {
    this._showWins();
  }

  private _onCellPanelSpinStopped(): void {
    if (
      gameEngine.gameActionData &&
      gameEngine.gameActionData.state === EGameState.HOLD_TO_MAIN
    ) {
      this._showWins();
    }
  }

  private _showWins(): void {
    console.log("spin stopped");
    this._activeWinDelayTween = gsap.delayedCall(1, () => {
      if (gameEngine.gameActionData.odd > 0.001) {
        EventSystem.invoke(constants.GameEvents.WINNING_POPUP_OPENED);
        Audio.fade(GameScreen.currentBgAudioName, 0.3);
      }

      if (gameEngine.gameActionData.odd >= constants.BetToWinRatios.SUPER) {
        this._playMegaWinAnimation();

        Audio.play("sndMegaWin");
      } else if (
        gameEngine.gameActionData.odd >= constants.BetToWinRatios.BIG
      ) {
        this._playSuperWinAnimation();

        Audio.play("sndSuperWin");
      } else if (
        gameEngine.gameActionData.odd >= constants.BetToWinRatios.NORMAL
      ) {
        this._playBigWinAnimation();

        Audio.play("sndBigWin");
      } else if (
        gameEngine.gameActionData.odd >= constants.BetToWinRatios.SMALL
      ) {
        this._playNormalWinAnimation();

        Audio.play("sndWin2");
      } else if (gameEngine.gameActionData.odd > 0.001) {

        this._playSmallWinAnimation();
        Audio.play("sndWin1");
      }

    });
  }

  private _onClickedInteractableArea(): void {
    if (!this._backgroundGraphics.visible) {
      return;
    }

    if (
      this._totalWinFadeAnimationTween &&
      this._totalWinFadeAnimationTween.isActive()
    ) {
      return;
    }

    // Skip the animations altogether, only display the final fade and hide animation.
    this._totalWinCountAnimationTween?.progress(1);

    if (this._totalWinFadeAnimationTween) {
      this._totalWinFadeAnimationTween.delay(0.5);
      this._totalWinFadeAnimationTween.duration(0.5);
      this._totalWinFadeAnimationTween.progress(0);

      // Since the hide animation is very slow, we need to slow
      // down the entire skeleton animation state.
      if (gameEngine.gameActionData.odd >= constants.BetToWinRatios.SUPER) {
        this._winSkeleton.state.timeScale = 0.5;
      } else if (
        gameEngine.gameActionData.odd >= constants.BetToWinRatios.BIG
      ) {
        this._winSkeleton.state.timeScale = 0.3;
      } else if (
        gameEngine.gameActionData.odd >= constants.BetToWinRatios.NORMAL
      ) {
        this._winSkeleton.state.timeScale = 0.2;
      }
    }
  }

  private _playSmallWinAnimation(): void {
    const winAmountTextPositionX: number = this._winAmountText.position.x;
    const winAmountTextPositionY: number = this._winAmountText.position.y;

    this._winAmountText.position.set(0);

    this._backgroundGraphics.visible = true;
    this._winAmountText.visible = true;

    this._winAmountText.text = gameEngine.formatter.format(
      gameEngine.gameActionData.roundWin,
    );

    this._totalWinFadeAnimationTween?.kill();
    this._totalWinFadeAnimationTween = gsap.fromTo(
      this._winAmountText,
      { alpha: 1.0 },
      {
        alpha: 0.0,
        delay: 1.5,
        duration: 1.0,
        ease: "power2.in",
        onComplete: () => {
          this._winAmountText.visible = false;
          this._winAmountText.alpha = 1;
          this._winAmountText.position.set(
            winAmountTextPositionX,
            winAmountTextPositionY,
          );
          this._backgroundGraphics.visible = false;

          Audio.fadeOutToEnd("sndWin1");
          Audio.fade(GameScreen.currentBgAudioName, 1.0);
          EventSystem.invoke(constants.GameEvents.WINNING_POPUP_CLOSED);
        },
      },
    );
  }

  private _playNormalWinAnimation(): void {
    const winAmountTextPositionX: number = this._winAmountText.position.x;
    const winAmountTextPositionY: number = this._winAmountText.position.y;

    this._winAmountText.position.set(0);

    this._playTextAnimation(2.2);

    this._backgroundGraphics.visible = true;
    this._winAmountText.visible = true;

    this._winAmountText.text = gameEngine.formatter.format(
      gameEngine.gameActionData.roundWin,
    );

    this._totalWinFadeAnimationTween?.kill();
    this._totalWinFadeAnimationTween = gsap.fromTo(
      this._winAmountText,
      { alpha: 1.0 },
      {
        alpha: 0.0,
        delay: 2.0,
        duration: 1.4,
        ease: "power2.in",
        onComplete: () => {
          this._winAmountText.visible = false;
          this._winAmountText.alpha = 1;
          this._winAmountText.position.set(
            winAmountTextPositionX,
            winAmountTextPositionY,
          );
          this._backgroundGraphics.visible = false;

          Audio.fadeOutToEnd("sndWin2");
          Audio.fade(GameScreen.currentBgAudioName, 1.0);
          EventSystem.invoke(constants.GameEvents.WINNING_POPUP_CLOSED);
        },
      },
    );
  }

  private _playBigWinAnimation(): void {
    this._backgroundGraphics.visible = true;
    this._winAmountText.visible = true;

    this._playTextAnimation(11.2);

    this._winSkeleton.state.setAnimation(0, "bigWinShow", false);
    this._winSkeleton.state.addAnimation(0, "bigWinLoop", true);

    this._totalWinFadeAnimationTween?.kill();
    this._totalWinFadeAnimationTween = gsap.fromTo(
      this._winAmountText,
      { alpha: 1 },
      {
        alpha: 0,
        delay: 12.0,
        duration: 0.3,
        ease: "power2.in",
        onStart: () => {
          this._winSkeleton.state.setAnimation(0, "bigWinHide", false);
        },
        onComplete: () => {
          this._winAmountText.visible = false;
          this._winAmountText.alpha = 1;
          this._backgroundGraphics.visible = false;
          Audio.fade("sndBigWin", 0.0, 1.0);
          Audio.play("sndBigWinEnd", false, 0.5);
          Audio.fade(GameScreen.currentBgAudioName, 1.0);
          EventSystem.invoke(constants.GameEvents.WINNING_POPUP_CLOSED);
        },
      },
    );
  }

  private _playSuperWinAnimation(): void {
    this._backgroundGraphics.visible = true;
    this._winAmountText.visible = true;

    this._playTextAnimation(15.2);

    this._winSkeleton.state.setAnimation(0, "bigWinShow", false);
    this._winSkeleton.state.addAnimation(0, "bigWinLoop", false);
    this._winSkeleton.state.addAnimation(0, "bigWinHide", false);
    this._winSkeleton.state.addAnimation(0, "superWinShow", false);
    this._winSkeleton.state.addAnimation(0, "superWinLoop", true);

    this._totalWinFadeAnimationTween?.kill();
    this._totalWinFadeAnimationTween = gsap.fromTo(
      this._winAmountText,
      { alpha: 1 },
      {
        alpha: 0,
        delay: 16.0,
        duration: 0.3,
        ease: "power2.in",
        onStart: () => {
          this._winSkeleton.state.setAnimation(0, "superWinHide", false);
        },
        onComplete: () => {
          this._winAmountText.visible = false;
          this._winAmountText.alpha = 1;
          this._backgroundGraphics.visible = false;
          Audio.fade("sndSuperWin", 0.0, 1.0);
          Audio.play("sndSuperWinEnd", false, 0.5);
          Audio.fade(GameScreen.currentBgAudioName, 1.0);
          EventSystem.invoke(constants.GameEvents.WINNING_POPUP_CLOSED);
        },
      },
    );
  }

  private _playMegaWinAnimation(): void {
    this._backgroundGraphics.visible = true;
    this._winAmountText.visible = true;

    this._playTextAnimation(22.2);

    this._winSkeleton.state.setAnimation(0, "bigWinShow", false);
    this._winSkeleton.state.addAnimation(0, "bigWinLoop", false);
    this._winSkeleton.state.addAnimation(0, "bigWinHide", false);
    this._winSkeleton.state.addAnimation(0, "superWinShow", false);
    this._winSkeleton.state.addAnimation(0, "superWinLoop", false);
    this._winSkeleton.state.addAnimation(0, "superWinHide", false);
    this._winSkeleton.state.addAnimation(0, "megaWinShow", false);
    this._winSkeleton.state.addAnimation(0, "megaWinLoop", true);

    this._totalWinFadeAnimationTween?.kill();
    this._totalWinFadeAnimationTween = gsap.fromTo(
      this._winAmountText,
      { alpha: 1 },
      {
        alpha: 0,
        delay: 23.0,
        duration: 0.3,
        ease: "power2.in",
        onStart: () => {
          this._winSkeleton.state.setAnimation(0, "megaWinHide", false);
        },
        onComplete: () => {
          this._winAmountText.visible = false;
          this._winAmountText.alpha = 1;
          this._backgroundGraphics.visible = false;
          Audio.fade("sndMegaWin", 0.0, 1.0);
          Audio.play("sndMegaWinEnd", false, 0.5);
          Audio.fade(GameScreen.currentBgAudioName, 1.0);
          EventSystem.invoke(constants.GameEvents.WINNING_POPUP_CLOSED);
        },
      },
    );
  }

  private _playTextAnimation(duration: number): void {
    const textValueObject = { value: 0 };

    if (gameEngine.gameActionData.odd >= constants.BetToWinRatios.SMALL) {
      Audio.play("sndCountUp", true, 2.0);
    }

    this._totalWinCountAnimationTween?.kill();
    this._totalWinCountAnimationTween = gsap.to(textValueObject, {
      value: gameEngine.gameActionData.roundWin,
      duration: duration,
      ease: "power4.out",
      onUpdate: () => {
        this._winAmountText.text = gameEngine.formatter.format(
          textValueObject.value,
        );
      },
      onComplete: () => {
        if (gameEngine.gameActionData.odd >= constants.BetToWinRatios.SMALL) {
          Audio.stop("sndCountUp");
          Audio.play("sndCountUpEnd", false, 2.0);
        }
      }
    });
  }

  private _onLandscapeLayoutRequested(): void {
    this._winSkeleton.scale.set(0.2);
    this._winAmountText.style.fontSize = 100;
    this._winAmountText.position.set(0, 200);
  }

  private _onPortraitLayoutRequested(): void {
    this._winSkeleton.scale.set(0.3);
    this._winAmountText.style.fontSize = 140;
    this._winAmountText.position.set(0, 250);
  }
}
